import React from 'react';

import EventBusyIcon from '@mui/icons-material/EventBusy';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { Stack, Typography } from '@mui/material';

import { EmptyState } from '@/components/NewUI/Components/CustomUIElements/EmptyState';

import { BOOKING_RULES } from './config';
import { styles } from './styles';

import type { ReactNode } from 'react';

export type DeadEnd = 'expired' | 'cancelled' | 'closed' | 'invalid';

interface IProps {
  state: DeadEnd;
  companyName: string;
}

const CONTENT: Record<
  DeadEnd,
  (companyName: string) => { icon: ReactNode; title: string; description: string }
> = {
  expired: (companyName) => ({
    icon: <HourglassEmptyIcon aria-hidden sx={styles.stateIcon} />,
    title: 'This booking link has expired',
    description: `Booking links last ${BOOKING_RULES.linkExpiryDays} days. If you’d still like to do the phone interview, please contact ${companyName} and ask for a new link.`
  }),
  cancelled: (companyName) => ({
    icon: <EventBusyIcon aria-hidden sx={styles.stateIcon} />,
    title: 'This interview has been cancelled',
    description: `${companyName} has cancelled this phone interview, so there’s nothing to book. If you think this is a mistake, please contact them.`
  }),
  closed: (companyName) => ({
    icon: <EventBusyIcon aria-hidden sx={styles.stateIcon} />,
    title: 'This booking link is closed',
    description: `${companyName} has already arranged your phone interview, so there’s nothing to book here. If you have questions, please contact them.`
  }),
  invalid: (companyName) => ({
    icon: <LinkOffIcon aria-hidden sx={styles.stateIcon} />,
    title: 'This booking link isn’t valid',
    description: `Check that you’ve opened the full link from your email. If it still doesn’t work, please contact ${companyName}.`
  })
};

// Dead ends use the shared EmptyState (icon + label) with one line of
// guidance underneath, as the candidate portal's PortalStates do.
const BookingDeadEnd = ({ state, companyName }: IProps) => {
  const { icon, title, description } = CONTENT[state](companyName);
  return (
    <Stack role="alert" sx={[styles.card, styles.stateCard]}>
      <EmptyState
        label={title}
        svgIcon={icon}
        textSx={styles.emptyStateText}
        wrapperSx={styles.emptyStateWrapper}
      />
      <Typography sx={styles.stateDescription}>{description}</Typography>
    </Stack>
  );
};

export default BookingDeadEnd;
